import { DagStatus } from '@agilelab/plugin-wb-builder-common';
import {
  GET_POLICIES_BY_IDS,
  governanceApiRef,
} from '@agilelab/plugin-wb-governance';
import { PoliciesByIdsQuery } from '@agilelab/plugin-wb-governance-common';
import {
  customAlertApiRef,
  GenericList,
  GenericListItem,
  TestStatus,
} from '@agilelab/plugin-wb-platform';
import { useApolloClient } from '@apollo/client';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { Typography } from '@material-ui/core';
import { WbTooltip } from '@agilelab/plugin-wb-platform';
import { ReactNode } from 'react';
import useAsync from 'react-use/lib/useAsync';

const statusIconStyle = {
  marginRight: '8px',
  display: 'flex',
  alignItems: 'center',
};

interface ViolationItem {
  policyName: string;
  policyVersion: string;
  policyDescription: string | null;
  outcome: DagStatus;
}

export function PolicyViolationsOverview({
  title,
  hideIfEmpty = false,
  noDataMessage,
  policyIdFilter,
  resource,
  wrapper,
}: {
  title?: string;
  hideIfEmpty?: boolean;
  noDataMessage?: string;
  policyIdFilter: string[];
  resource: {
    externalId: string;
    environment: string;
  };
  wrapper?: (children: ReactNode) => JSX.Element;
}): JSX.Element {
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);
  const governanceApi = useApi(governanceApiRef);
  const apolloClient = useApolloClient();

  const {
    loading,
    value: violationItems,
    error,
  } = useAsync(async () => {
    if (!policyIdFilter.length) return [];

    const { violations } = await governanceApi.getPolicyViolations(
      {
        resource: resource.externalId,
        environment: resource.environment,
        policies: policyIdFilter,
      },
      await identityApi.getCredentials(),
    );

    if (!violations.length) return [];

    const uniquePolicyIds = [...new Set(violations.map(v => v.policyId))];

    const { data } = await apolloClient.query<PoliciesByIdsQuery>({
      query: GET_POLICIES_BY_IDS,
      variables: { policyIds: uniquePolicyIds },
    });

    const policiesMap = new Map(
      data.cgp_governance_entity.map(policy => [
        policy.governance_entity_id,
        policy,
      ]),
    );

    return violations
      .map(violation => {
        const policy = policiesMap.get(violation.policyId);

        if (!policy) {
          alertApi.post({
            message: `Policy not found:  ${violation.policyId}`,
          });

          return undefined;
        }

        return {
          policyName: policy.name,
          policyVersion: `v:${policy.version}`,
          policyDescription: policy.description,
          outcome: violation.outcome.toUpperCase() as DagStatus,
        };
      })
      .filter(Boolean) as ViolationItem[];
  }, [
    governanceApi,
    identityApi,
    apolloClient,
    resource.externalId,
    resource.environment,
    policyIdFilter,
  ]);

  const items: GenericListItem[] | undefined = violationItems?.map(v => ({
    icon: (
      <TestStatus
        style={statusIconStyle}
        status={v.outcome}
        filled
        variant="reduced"
      />
    ),
    text: (
      <WbTooltip title={v.policyDescription ?? v.policyName}>
        <Typography>{`${v.policyName} ${v.policyVersion}`}</Typography>
      </WbTooltip>
    ),
  }));

  if (hideIfEmpty && (loading || (items && !items.length))) return <></>;

  const list = (
    <GenericList
      label={title}
      isLoading={loading}
      isError={!!error}
      error={
        error && (
          <Typography>Policy violations are temporarily unavailable</Typography>
        )
      }
      items={items}
      noData={<Typography>{noDataMessage ?? 'No violations'}</Typography>}
    />
  );

  return wrapper ? wrapper(list) : list;
}
